import useScrollReveal from "@/hooks/useScrollReveal";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessSectionProps {
  tagline: string;
  title: string;
  subtitle: string;
  steps: ProcessStep[];
  ctaText: string;
  ctaHref: string;
}

export default function ProcessSection({
  tagline,
  title,
  subtitle,
  steps,
  ctaText,
  ctaHref,
}: ProcessSectionProps) {
  const headRef = useScrollReveal<HTMLDivElement>();
  const stepsRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal py-32 px-6 lg:px-14 relative overflow-hidden">
      {/* Atmospheric noise */}
      <div className="grain-overlay absolute inset-0 pointer-events-none" aria-hidden="true" />

      {/* Faint warm glow, top-left */}
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse at top left, rgba(201,168,76,0.05) 0%, transparent 65%)" }}
      />

      {/* Gold hairline top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Header */}
        <div ref={headRef} className="sr-hidden mb-20">
          <div className="flex items-center gap-5 mb-6">
            <span className="inline-block w-12 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.28em]">{tagline}</p>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[64px] text-white font-bold mb-6 max-w-2xl leading-[0.95]">
            {title}
          </h2>
          <p className="text-stone-400 font-sans text-base max-w-lg leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Steps — timeline style */}
        <div ref={stepsRef} className="sr-hidden sr-delay-2 mb-20">

          {/* Desktop: horizontal with connector lines */}
          <div className="hidden lg:block relative">
            {/* Horizontal connector line */}
            <div className="absolute top-[52px] left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-px bg-gradient-to-r from-gold/30 via-gold/50 to-gold/30" />

            <div className="grid grid-cols-4 gap-0">
              {steps.map((step, i) => (
                <div key={i} className="relative flex flex-col items-center text-center px-6 pt-0 group">
                  {/* Number node — sits on the connector line */}
                  <div className="relative mb-10 z-10">
                    <div className="w-[52px] h-[52px] flex items-center justify-center border border-stone-700 bg-charcoal group-hover:border-gold/60 transition-colors duration-400">
                      <span className="font-display text-gold text-sm font-bold">{step.number}</span>
                    </div>
                    {/* Pulse dot */}
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Content */}
                  <div className="border-t border-stone-800/80 pt-8 w-full group-hover:border-stone-700 transition-colors duration-400">
                    {/* Large faded number */}
                    <div
                      className="font-display text-stone-800 font-bold leading-none mb-5 select-none group-hover:text-stone-700 transition-colors duration-500"
                      style={{ fontSize: "56px", lineHeight: 1 }}
                      aria-hidden="true"
                    >
                      {step.number}
                    </div>
                    <div className="w-6 h-0.5 bg-gold mx-auto mb-5" />
                    <h3 className="font-display text-white text-lg font-bold mb-3 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-stone-400 font-sans text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="lg:hidden relative flex flex-col gap-0">
            {/* Vertical connector */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-gold/20 via-gold/40 to-gold/20" />

            {steps.map((step, i) => (
              <div key={i} className="relative flex gap-8 pb-12 last:pb-0">
                {/* Node */}
                <div className="relative flex-shrink-0 z-10">
                  <div className="w-12 h-12 flex items-center justify-center border border-stone-700 bg-charcoal">
                    <span className="font-display text-gold text-xs font-bold">{step.number}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="pt-2 flex-1">
                  <div className="w-5 h-0.5 bg-gold mb-4" />
                  <h3 className="font-display text-white text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-stone-400 font-sans text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href={ctaHref}
            className="group inline-flex items-center gap-3 px-12 py-4 bg-gold text-white font-sans font-bold text-[11px] uppercase tracking-[0.22em] hover:bg-gold-dark transition-all whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 8px 32px rgba(201,168,76,0.2)" }}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill"></i>
            </span>
            {ctaText}
          </a>
        </div>
      </div>
    </section>
  );
}
